import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { appForPath, DEFAULT_APP, DOCUMENT_MANIFEST } from "./homeScreenApps";
import {
  hasInstallPrompt,
  onInstallPromptChange,
  showInstallPrompt,
} from "./installPrompt";

const PENDING_KEY = "a2hs:pending";
const dismissKey = (path) => `a2hs:dismissed:${path}`;

function isStandalone() {
  return (
    window.navigator.standalone === true ||
    window.matchMedia("(display-mode: standalone)").matches
  );
}

function isIos() {
  return /iphone|ipad|ipod/i.test(window.navigator.userAgent);
}

function readFlag(storage, key) {
  try {
    return window[storage].getItem(key);
  } catch {
    return null; // Safari private mode throws on storage access.
  }
}

function writeFlag(storage, key, value) {
  try {
    if (value === null) window[storage].removeItem(key);
    else window[storage].setItem(key, value);
  } catch {
    /* ignore */
  }
}

/*
 * Offers a per-section "Add to Home Screen" button on /shopping,
 * /weekly-plan, /macro-calculator and /workout-tracker.
 *
 * The catch this works around: iOS reads the manifest once, while the
 * document loads. Arriving on a section via client-side routing therefore
 * leaves the *root* manifest in effect, and saving from there produces an
 * icon that opens "/" - which is exactly the bug this component exists to
 * prevent. So if the manifest the document actually loaded with isn't this
 * section's, the button does a full page navigation first and picks the
 * instructions back up after the reload.
 */
export default function AddToHomeScreen() {
  const { pathname } = useLocation();
  const app = appForPath(pathname);
  const isSection = app !== DEFAULT_APP;

  const [showSteps, setShowSteps] = useState(false);
  const [dismissed, setDismissed] = useState(true);
  const [canPrompt, setCanPrompt] = useState(hasInstallPrompt());

  useEffect(() => onInstallPromptChange(setCanPrompt), []);

  useEffect(() => {
    if (!isSection) return;
    setDismissed(Boolean(readFlag("localStorage", dismissKey(app.path))));
    // Resuming after the reload below: show the steps straight away.
    if (readFlag("sessionStorage", PENDING_KEY) === app.path) {
      writeFlag("sessionStorage", PENDING_KEY, null);
      setShowSteps(true);
    } else {
      setShowSteps(false);
    }
  }, [isSection, app.path]);

  if (!isSection || dismissed || isStandalone()) return null;

  const handleAdd = async () => {
    if (canPrompt) {
      await showInstallPrompt();
      return;
    }
    if (DOCUMENT_MANIFEST !== app.manifest) {
      writeFlag("sessionStorage", PENDING_KEY, app.path);
      window.location.assign(app.path);
      return;
    }
    setShowSteps(true);
  };

  const handleDismiss = () => {
    writeFlag("localStorage", dismissKey(app.path), "1");
    setDismissed(true);
  };

  return (
    <div className="alert alert-secondary d-flex flex-wrap align-items-center gap-2 py-2 px-3 mb-3">
      <img
        src={app.appleTouchIcon}
        alt=""
        width="28"
        height="28"
        style={{ borderRadius: 6 }}
      />
      <span className="me-auto">
        {showSteps ? (
          isIos() ? (
            <>
              Tap <strong>Share</strong> at the bottom of Safari, then{" "}
              <strong>Add to Home Screen</strong>. It will be named{" "}
              <strong>{app.title}</strong> and open straight to this page.
            </>
          ) : (
            <>
              Use your browser menu and choose <strong>Install</strong> or{" "}
              <strong>Add to Home screen</strong> to save{" "}
              <strong>{app.title}</strong>.
            </>
          )
        ) : (
          <>
            Add <strong>{app.title}</strong> to your home screen to open it in
            one tap.
          </>
        )}
      </span>
      {!showSteps && (
        <button
          type="button"
          className="btn btn-sm btn-dark"
          onClick={handleAdd}
        >
          Add to Home Screen
        </button>
      )}
      <button
        type="button"
        className="btn-close ms-1"
        aria-label="Dismiss"
        onClick={handleDismiss}
      />
    </div>
  );
}
